import { makeAssistantToolUI } from '@assistant-ui/react';
import { Circle, CircleCheck, CircleDot, CircleMinus, ListChecks } from 'lucide-react';
import { ToolRow } from '../toolRow';

type TodoStatus = 'pending' | 'in_progress' | 'completed' | 'cancelled';

interface Todo {
  id: string;
  content: string;
  status: TodoStatus;
}

interface TodosArgs {
  todos: Todo[];
  merge?: boolean;
  _thought?: string;
}

interface TodosResult {
  success?: boolean;
  error?: string;
  todos?: Todo[];
  total?: number;
  pending?: number;
  in_progress?: number;
  completed?: number;
  cancelled?: number;
  merge?: boolean;
}

/**
 * Inline checklist card rendered at the point `todo_write` was invoked.
 *
 * Each call captures the state of the list at that moment — earlier cards in
 * the stream are historical snapshots, the latest one is the current view.
 * The canonical list also lives on `thread.todos` server-side for durable
 * prompt injection, but the UI renders strictly from tool results.
 */
export const TodosToolUI = makeAssistantToolUI<TodosArgs, TodosResult>({
  toolName: 'todo_write',
  render: function TodosToolRender({ args, result, status }) {
    const isRunning = status.type === 'running';
    const errored = !!result?.error;

    // Prefer the tool result's canonical list (server has applied merge
    // semantics) over the raw args, which only contain the incoming delta
    // when merge=true.
    const listFromResult = result?.todos;
    const listFromArgs = args?.todos;
    const raw: Todo[] = Array.isArray(listFromResult)
      ? listFromResult
      : Array.isArray(listFromArgs)
        ? listFromArgs
        : [];

    // Sort cancelled to the bottom so the active plan stays visible.
    const todos = [...raw].sort((a, b) => {
      const aCancelled = a.status === 'cancelled' ? 1 : 0;
      const bCancelled = b.status === 'cancelled' ? 1 : 0;
      return aCancelled - bCancelled;
    });

    const total = todos.length;
    const completed = todos.filter((t) => t.status === 'completed').length;

    return (
      <div className="ai-todos my-1">
        <ToolRow
          icon={<ListChecks />}
          label={isRunning ? 'Updating plan' : errored ? 'Plan update failed' : 'Plan'}
          meta={!isRunning && !errored && total > 0 ? `${completed}/${total}` : undefined}
          tone={errored ? 'error' : 'default'}
          running={isRunning}
          open={false}
        />
        {errored ? (
          <div className="pl-[22px] text-meta text-red-700">{result?.error}</div>
        ) : todos.length > 0 ? (
          <ul className="space-y-0.5 pb-1 pl-[22px]">
            {todos.map((t) => (
              <li key={t.id} className="flex items-start gap-2 text-meta">
                <TodoIcon status={t.status} />
                <span className={contentClass(t.status, isRunning)}>{t.content}</span>
              </li>
            ))}
          </ul>
        ) : (
          <div className="pl-[22px] text-meta text-zinc-400">Empty list.</div>
        )}
      </div>
    );
  },
});

function TodoIcon({ status }: { status: TodoStatus }) {
  const base = 'w-3.5 h-3.5 mt-[3px] shrink-0';
  switch (status) {
    case 'in_progress':
      return <CircleDot className={`${base} text-accent-500`} />;
    case 'completed':
      return <CircleCheck className={`${base} text-emerald-600`} />;
    case 'cancelled':
      return <CircleMinus className={`${base} text-zinc-300`} />;
    case 'pending':
    default:
      return <Circle className={`${base} text-zinc-300`} />;
  }
}

function contentClass(status: TodoStatus, isRunning: boolean): string {
  if (status === 'completed') return 'text-zinc-400 line-through decoration-zinc-300';
  if (status === 'cancelled') return 'text-zinc-400 line-through decoration-zinc-300';
  if (status === 'in_progress') return 'text-zinc-900';
  if (isRunning) return 'text-zinc-500';
  return 'text-zinc-600';
}
